import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, RefreshCw, CheckSquare, Square, Layers } from 'lucide-react';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const abs = (u) => (!u ? null : u.startsWith('http') ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);
const thumb = (u) => (u && /\.(jpe?g|png|webp)$/i.test(u) ? `${abs(u)}?w=320` : abs(u));

const STATUS_STYLE = {
  passed: 'bg-emerald-100 text-emerald-800', skipped: 'bg-amber-100 text-amber-800', pending: 'bg-neutral-200 text-neutral-700',
};

const RenderCard = ({ r, selected, onToggle, onRetry, busy }) => (
  <div className={`rounded-lg border bg-white overflow-hidden flex flex-col ${selected ? 'ring-2 ring-neutral-900' : ''}`} data-testid={`sr-card-${r.color_id}-${r.room}`}>
    <div className="grid grid-cols-2 gap-px bg-neutral-200">
      <img src={thumb(r.image_url)} alt="" className="w-full aspect-[4/3] object-cover" loading="lazy" />
      {r.clean_url
        ? <img src={thumb(r.clean_url)} alt="" className="w-full aspect-[4/3] object-cover" loading="lazy" />
        : <div className="w-full aspect-[4/3] bg-neutral-100 flex items-center justify-center text-[11px] text-neutral-500">no clean background</div>}
    </div>
    <div className="p-3 flex-1 flex flex-col gap-1.5">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-medium truncate">{r.color}</p>
          <p className="text-xs text-neutral-500 truncate">{r.collection} · {r.room.replace('_', ' ')}</p>
        </div>
        <span className={`text-[11px] px-2 py-0.5 rounded-full shrink-0 ${STATUS_STYLE[r.status]}`} data-testid={`sr-status-${r.color_id}-${r.room}`}>{r.status}</span>
      </div>
      {r.status === 'passed' && <p className="text-xs text-neutral-600">{r.panels} panel{r.panels === 1 ? '' : 's'}{r.method === 'raised' ? ' · raised-shade render' : ''}{r.retried ? ' · after retry' : ''}</p>}
      {r.reason && <p className="text-xs text-amber-800 leading-snug" data-testid={`sr-reason-${r.color_id}-${r.room}`}>{r.reason}</p>}
      <div className="mt-auto flex items-center gap-2 pt-1">
        <button type="button" onClick={onToggle} className="text-neutral-600" data-testid={`sr-select-${r.color_id}-${r.room}`}>{selected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}</button>
        <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy} onClick={() => onRetry('edit')} data-testid={`sr-retry-${r.color_id}-${r.room}`}>Retry edit</Button>
        <Button size="sm" className="h-7 text-xs" disabled={busy} onClick={() => onRetry('raised')} data-testid={`sr-raised-${r.color_id}-${r.room}`}>Raised render</Button>
      </div>
    </div>
  </div>
);

export const ShadeRegionsPanel = ({ token }) => {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [renders, setRenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('skipped');
  const [collection, setCollection] = useState('all');
  const [selected, setSelected] = useState(() => new Set());
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/admin/fabrics/shade-regions/renders`, { headers });
      setRenders(data.renders);
    } catch (e) { toast.error('Could not load shade-region renders'); }
    setLoading(false);
  }, [headers]);

  const pollJob = useCallback((id) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API_URL}/admin/fabrics/shade-regions/job/${id}/summary`, { headers });
        setJob(data);
        if (data.status !== 'running') {
          clearInterval(pollRef.current);
          toast.success(`Job finished: ${data.passed} passed · ${data.skipped} skipped · ${data.failed} failed`);
          load();
        }
      } catch (e) { clearInterval(pollRef.current); }
    }, 5000);
  }, [headers, load]);

  useEffect(() => { load(); return () => clearInterval(pollRef.current); }, [load]);
  useEffect(() => {
    axios.get(`${API_URL}/admin/fabrics/shade-regions/status`, { headers }).then(({ data }) => {
      if (data.latest_job) setJob(data.latest_job);
      if (data.latest_job?.status === 'running') pollJob(data.latest_job.id);
      else if (data.latest_job?.id) axios.get(`${API_URL}/admin/fabrics/shade-regions/job/${data.latest_job.id}/summary`, { headers }).then(r => setJob(r.data)).catch(() => {});
    }).catch(() => {});
  }, [headers, pollJob]);

  const retry = async (items, method) => {
    try {
      const { data } = await axios.post(`${API_URL}/admin/fabrics/shade-regions/retry`, { items, method }, { headers });
      setJob({ ...data, passed: 0, skipped: 0, failed: 0 });
      setSelected(new Set());
      toast.info(`${method === 'raised' ? 'Raised-shade render' : 'Background edit'} started for ${items.length} render${items.length === 1 ? '' : 's'}`);
      pollJob(data.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Retry failed'); }
  };

  const counts = useMemo(() => renders.reduce((a, r) => ({ ...a, [r.status]: (a[r.status] || 0) + 1 }), { passed: 0, skipped: 0, pending: 0 }), [renders]);
  const collections = useMemo(() => [...new Set(renders.map(r => r.collection))].sort(), [renders]);
  const shown = renders.filter(r => (filter === 'all' || r.status === filter) && (collection === 'all' || r.collection === collection));
  const busy = job?.status === 'running';
  const key = (r) => `${r.color_id}|${r.room}`;
  const toggle = (r) => setSelected(prev => { const n = new Set(prev); n.has(key(r)) ? n.delete(key(r)) : n.add(key(r)); return n; });
  const selectedItems = renders.filter(r => selected.has(key(r))).map(r => ({ color_id: r.color_id, room: r.room }));

  return (
    <div className="space-y-4" data-testid="shade-regions-panel">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-lg font-semibold mr-2">Shade Regions</h2>
        {['all', 'passed', 'skipped', 'pending'].map(s => (
          <button key={s} type="button" onClick={() => setFilter(s)} className={`text-xs px-3 py-1 rounded-full border ${filter === s ? 'bg-neutral-900 text-white border-neutral-900' : 'bg-white text-neutral-700'}`} data-testid={`sr-filter-${s}`}>
            {s} {s === 'all' ? renders.length : counts[s] || 0}
          </button>
        ))}
        <select value={collection} onChange={e => setCollection(e.target.value)} className="text-xs border rounded-md px-2 py-1 bg-white" data-testid="sr-collection-filter">
          <option value="all">All collections</option>
          {collections.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <Button size="sm" variant="outline" onClick={load} disabled={loading} className="h-7 text-xs" data-testid="sr-refresh"><RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />Refresh</Button>
        {(counts.pending > 0 || counts.skipped > 0) && (
          <Button size="sm" variant="default" className="h-7 text-xs bg-amber-600 hover:bg-amber-700" disabled={busy}
            data-testid="sr-detect-all-missing"
            onClick={() => {
              const missing = renders.filter(r => r.status === 'pending' || r.status === 'skipped').map(r => ({ color_id: r.color_id, room: r.room }));
              if (missing.length) retry(missing, 'edit');
            }}>
            Detect All Missing ({(counts.pending || 0) + (counts.skipped || 0)})
          </Button>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setSelected(new Set(shown.map(key)))} data-testid="sr-select-shown"><Layers className="w-3.5 h-3.5 mr-1" />Select shown ({shown.length})</Button>
          <Button size="sm" variant="outline" className="h-7 text-xs" disabled={!selectedItems.length || busy} onClick={() => retry(selectedItems, 'edit')} data-testid="sr-bulk-retry-edit">Retry edit ({selectedItems.length})</Button>
          <Button size="sm" className="h-7 text-xs" disabled={!selectedItems.length || busy} onClick={() => retry(selectedItems, 'raised')} data-testid="sr-bulk-retry-raised">Raised render ({selectedItems.length})</Button>
        </div>
      </div>

      {job && (
        <div className="rounded-lg border bg-neutral-50 px-4 py-2 text-xs flex items-center gap-3" data-testid="sr-job-status">
          {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          <span className="font-medium">{busy ? 'Running' : 'Last job'} · {job.method || 'edit'}</span>
          <span>{job.completed}/{job.total}</span>
          <span className="text-emerald-700">passed {job.passed ?? 0}</span>
          <span className="text-amber-700">skipped {job.skipped ?? 0}</span>
          <span className="text-red-700">failed {job.failed ?? 0}</span>
        </div>
      )}

      {loading ? <div className="py-10 text-center text-sm text-neutral-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading renders…</div>
        : shown.length === 0 ? <p className="text-sm text-neutral-500 py-8 text-center" data-testid="sr-empty">No renders match this filter</p>
        : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="sr-grid">
            {shown.map(r => <RenderCard key={key(r)} r={r} selected={selected.has(key(r))} onToggle={() => toggle(r)} busy={busy} onRetry={(m) => retry([{ color_id: r.color_id, room: r.room }], m)} />)}
          </div>}
    </div>
  );
};

export default ShadeRegionsPanel;
